import cx from "classnames";
import { LocationDescriptor } from "history";
import React, { useEffect, useRef } from "react";

import { CategoryKey } from "@/mk2/apps/bazaar/components/CategoryFacetOption";
import CategoryFacetSubOption from "@/mk2/apps/bazaar/components/CategoryFacetSubOption";
import { getLastRowIndexStart } from "@/mk2/helpers/grid";

import styles from "./CategoryFacetSubOptions.mscss";

const ROW_LENGTH = 4;
const ROW_LENGTH_MOBILE = 2;

export interface SubcategoryOption {
    key: CategoryKey;
    count?: number;
    to?: LocationDescriptor;
    children?: SubcategoryOption[];
}

export interface OwnProps {
    className?: string;
    options?: SubcategoryOption[];
    selected?: CategoryKey;
    opened?: CategoryKey;

    onSelect?(category: CategoryKey);
    onToggle?(category: CategoryKey);
}

type Props = OwnProps;

const childMatch = (s, key): boolean =>
    s.key === key || s.children?.some((c) => childMatch(c, key));

const CategoryFacetSubOptions: React.FunctionComponent<Props> = ({
    className,
    options,
    selected = null,
    onSelect,
    onToggle,
    opened,
}) => {
    const openedIndex = options.findIndex(
        (o) => o.key === opened || o.children?.some((c) => childMatch(c, opened)),
    );
    const openedOption = options[openedIndex];
    const openedDesktopIndex =
        openedIndex > -1 && Math.ceil((openedIndex + 1) / ROW_LENGTH) * ROW_LENGTH;
    const openedMobileIndex =
        openedIndex > -1 && Math.ceil((openedIndex + 1) / ROW_LENGTH_MOBILE) * ROW_LENGTH_MOBILE;

    const containerRef = useRef<HTMLDivElement>();
    const toFill = (4 - (options.length % 4)) % 4;
    const lastRowStartDesktopIndex = getLastRowIndexStart(options, ROW_LENGTH);
    const lastRowStartMobileIndex = getLastRowIndexStart(options, ROW_LENGTH_MOBILE);

    // Scroll to viewport when opened
    useEffect(() => {
        const bounding = containerRef.current.getBoundingClientRect();
        if (bounding.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
            // Bottom not in viewport
            containerRef.current.scrollIntoView(false);
        }
    }, []);

    return (
        <div className={cx(styles.CategoryFacetSubOptions, className)} ref={containerRef}>
            <div className={styles.CategoryFacetSubOptions__container}>
                {options.map((opt, index) => (
                    <React.Fragment key={opt.key}>
                        {(index === openedDesktopIndex || index === openedMobileIndex) && (
                            <div
                                className={
                                    styles[
                                        `CategoryFacetSubOptions__suboptions--${
                                            openedDesktopIndex === openedMobileIndex
                                                ? ""
                                                : index === openedDesktopIndex
                                                  ? "desktop"
                                                  : "mobile"
                                        }`
                                    ]
                                }
                            >
                                <CategoryFacetSubOptions
                                    options={openedOption.children}
                                    opened={opened}
                                    onSelect={onSelect}
                                    selected={selected}
                                    onToggle={onToggle}
                                />
                            </div>
                        )}
                        <CategoryFacetSubOption
                            className={cx(
                                index >= lastRowStartDesktopIndex &&
                                    styles[`CategoryFacetSubOptions__row_last--desktop`],
                                index >= lastRowStartMobileIndex &&
                                    styles[`CategoryFacetSubOptions__row_last--mobile`],
                            )}
                            key={opt.key}
                            category={opt.key}
                            count={opt.count}
                            to={opt.to}
                            selected={
                                selected === opt.key ||
                                opt.children?.some((s) => childMatch(s, selected))
                            }
                            dimmed={openedOption && openedOption !== opt}
                            opened={openedOption === opt}
                            onSelect={opt.children?.length > 0 ? onToggle : onSelect}
                            hasSubcategories={opt.children?.length > 0}
                        />
                    </React.Fragment>
                ))}
                {Array.from(Array(toFill).keys()).map((index) => (
                    <div
                        key={`empty-${index}`}
                        className={cx(
                            styles.CategoryFacetSubOptions__empty,
                            index === 0 &&
                                (toFill === 1 || toFill === 3) &&
                                styles[`CategoryFacetSubOptions__row_last--mobile`], // on mobile we can have max 1 empty placeholder
                            (index !== 0 || toFill === 2) &&
                                styles[`CategoryFacetSubOptions__row_last--mobile--hide`], // on mobile we can have max 1 empty placeholder
                            openedOption && styles["CategoryFacetSubOptions__empty--dimmed"],
                        )}
                    />
                ))}
                {(options.length <= openedDesktopIndex || options.length <= openedMobileIndex) && (
                    <div
                        className={
                            styles[
                                `CategoryFacetSubOptions__suboptions--${
                                    options.length <= openedMobileIndex
                                        ? "" // Display on both
                                        : "desktop" // Display only on desktop
                                }`
                            ]
                        }
                    >
                        <CategoryFacetSubOptions
                            options={openedOption.children}
                            opened={opened}
                            onSelect={onSelect}
                            selected={selected}
                            onToggle={onToggle}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

CategoryFacetSubOptions.displayName = "CategoryFacetSubOptions";

export default React.memo<Props>(CategoryFacetSubOptions);
